import { useTranslation } from 'react-i18next';
import FlexBox from '@ess/ui/FlexBox';
import Text from '@ess/ui/Text';
import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { showToast, TOAST_ERROR } from '@ess/utils';
import { isNumber, toNumber } from 'lodash-es';
import { Styled } from './NumberInput.styles';

type NumberInputProps = {
  value: string
  codeLength: 1 | 2 | 3 | 4 | 5 | 6
  isAutoFocus?: boolean
  onChange: (value: string) => void
  onEnterPress?: () => void
  size?: 'small' | 'medium' | 'large'
}

const boxSizes: Record<string, { width: number, height: number, font: number }> = {
  small: { width: 20, height: 30, font: 19 },
  medium: { width: 30, height: 40, font: 23 },
  large: { width: 40, height: 50, font: 27 },
};

const emptyBoxValue = '';

const NumberInput = ({
  codeLength,
  isAutoFocus = false,
  onEnterPress = undefined,
  size = 'large',
  onChange,
  value,
}: NumberInputProps) => {
  const { t } = useTranslation();

  const refList = useRef<any>({});

  const getBoxValue = useCallback((boxIndex: number) => {
    if (value === undefined) {
      return emptyBoxValue;
    }

    if (value[boxIndex] === ' ') {
      return emptyBoxValue;
    }

    return value.toString()[boxIndex] || emptyBoxValue;
  }, [value]);

  const onChangeHandler = (event: any, boxIndex: number) => {
    const inputValue = event.target.value;

    if (inputValue.length > 1) {
      const nextIndex = boxIndex + 1;
      const isNext = refList.current[nextIndex];
      if (isNext) {
        refList.current[nextIndex]?.focus();
      }
    }

    if (Number.isNaN(toNumber(inputValue)) || toNumber(inputValue) < 0 || inputValue.length > 1) {
      return;
    }

    let newValue = '';
    for (let i = 0; i < codeLength; i++) {
      if (i === boxIndex) {
        if (inputValue !== '') {
          newValue += inputValue;
        } else {
          newValue += ' ';
        }
      } else {
        const currentValue = value?.toString()[i];
        if (currentValue !== undefined && currentValue !== ' ') {
          newValue += currentValue.toString();
        } else {
          newValue += ' ';
        }
      }
    }
    onChange(newValue);

    if (inputValue !== '') {
      const nextIndex = boxIndex + 1;
      const isNext = refList.current[nextIndex];
      if (isNext) {
        refList.current[nextIndex]?.focus();
      }
    } else {
      const previousIndex = boxIndex - 1;
      const isPrevious = refList.current[previousIndex];
      if (isPrevious) {
        refList.current[previousIndex]?.focus();
      }
    }
  };

  const onKeyPressCapture = (event: any, boxIndex: number) => {
    if (event.key === 'Enter' && boxIndex === codeLength - 1) {
      if (onEnterPress) {
        onEnterPress();
        refList.current[boxIndex]?.blur();
      }
    }
  };

  const onPasteHandler = (event: any, boxIndex: number) => {
    event.preventDefault();
    const pastedValue: number = event.clipboardData.getData('Text').trim().slice(0, codeLength);

    if (!Number.isNaN(toNumber(pastedValue))) {
      const newValue = ((value || '') + pastedValue.toString());
      onChange(newValue);
    } else {
      showToast(TOAST_ERROR, t('lbl_passed_content_not_numbers_only'));
    }
  };

  useEffect(() => {
    if (isAutoFocus && refList?.current[0]) {
      setTimeout(() => {
        refList?.current[0]?.focus();
      }, 50);
    }
  }, []);

  return (
    <FlexBox
      style={{
        gap: '8px',
        padding: '10px',
        alignItems: 'center',
        alignContent: 'center',
        alignSelf: 'center',
      }}
    >

      {Array.from({ length: codeLength })
        .map((_, index: number) => {
          const { width, height, font } = boxSizes[size];

          return (
            <Styled.SingleInput
              ref={(el) => {
                refList.current[index] = el;
              }}
              onKeyPressCapture={(event: any) => onKeyPressCapture(event, index)}
              inputMode="numeric"
              placeholder={`${index + 1}`}
              onPaste={(event: any) => onPasteHandler(event, index)}
              onChange={(event: any) => onChangeHandler(event, index)}
              width={width}
              font={font}
              value={getBoxValue(index)}
              style={{
                height: `${height}px`,
              }}
            />
          );
        },
        )}
    </FlexBox>
  );
};

export default NumberInput;
